import * as React from "react";
import { Sparkles, X, AlertTriangle, HelpCircle, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";

interface ContainerSpace { id: string; name: string; type: string; parentId: string | null }
interface SpaceItem { id: string; name: string }

interface Assignment {
  containerId: string;
  containerLabel: string;
  fill: number;
  fit: "comfortable" | "tight" | "wont_fit";
  fitConfidence: number;
  items: { itemId: string; name: string }[];
  reasoning: string;
}
interface ReorgResult {
  assignments: Assignment[];
  overflow: { itemId: string; name: string; reason: string }[];
  lowConfidence: { itemId: string; name: string; hint: string }[];
  alreadyOptimal: { itemId: string; name: string; containerId: string }[];
  fits: "yes" | "likely" | "no" | "unknown";
  containersMissingDimensions: { containerId: string; label: string }[];
}

const FIT_STYLES: Record<Assignment["fit"], string> = {
  comfortable: "bg-green-50 text-green-700 border-green-200",
  tight: "bg-amber-50 text-amber-700 border-amber-200",
  wont_fit: "bg-red-50 text-red-700 border-red-200",
};
const FIT_LABEL: Record<Assignment["fit"], string> = { comfortable: "Comfortable", tight: "Tight", wont_fit: "Won't fit" };

/**
 * Flow #1 — "Reorganize across containers", scoped to container-type SPACES
 * (closets/drawers/shelves), NOT boxes. Opened from a container's detail page:
 * redistribute the items here across this container and its sibling containers.
 * The engine decides; the AI only narrates. Applying moves items via item.spaceId.
 */
export function ReorganizeContainersPanel({
  container,
  items,
  onClose,
  onApplied,
}: {
  container: { id: string; name: string; parentId: string | null };
  items: SpaceItem[];
  onClose: () => void;
  onApplied: () => void;
}) {
  const toast = useToast();
  const [siblings, setSiblings] = React.useState<ContainerSpace[]>([]);
  const [selectedContainerIds, setSelectedContainerIds] = React.useState<Set<string>>(new Set([container.id]));
  const [selectedItemIds, setSelectedItemIds] = React.useState<Set<string>>(new Set(items.map((i) => i.id)));
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<ReorgResult | null>(null);
  const [applying, setApplying] = React.useState(false);

  // Load sibling container spaces (same parent) as additional redistribution targets.
  React.useEffect(() => {
    apiFetch<ContainerSpace[]>("/api/spaces")
      .then((all) => setSiblings(all.filter((s) => s.type === "container" && s.id !== container.id && s.parentId === container.parentId)))
      .catch(() => {});
  }, [container.id, container.parentId]);

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };

  const canRun = selectedContainerIds.size > 0 && selectedItemIds.size > 0 && !loading;

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await apiFetch<ReorgResult>("/api/ai/reorganize", {
        method: "POST",
        body: JSON.stringify({ itemIds: [...selectedItemIds], containerIds: [...selectedContainerIds], containerType: "container_space" }),
      });
      setResult(res);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 404) { toast.show("Reorganize isn't available yet.", "error"); onClose(); }
      else if (status === 429) { toast.show("You've hit your AI limit for this month.", "error"); }
      else { toast.show("Couldn't build a plan. Try again.", "error"); }
    }
    setLoading(false);
  };

  const apply = async () => {
    if (!result) return;
    setApplying(true);
    try {
      // Container-space membership is item.spaceId — move each item with a PUT.
      for (const a of result.assignments) {
        for (const it of a.items) {
          await apiFetch(`/api/items/${it.itemId}`, { method: "PUT", body: JSON.stringify({ spaceId: a.containerId }) });
        }
      }
      toast.show("Reorganize applied.");
      onApplied();
      onClose();
    } catch {
      toast.show("Some items couldn't be moved. Please review.", "error");
    }
    setApplying(false);
  };

  const allContainers = [{ id: container.id, name: container.name }, ...siblings];

  return (
    <div className="bg-white rounded-2xl border border-primary/25 p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Reorganize containers</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-400" /></button>
      </div>

      {!result && (
        <>
          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">Redistribute across</p>
            <div className="flex flex-wrap gap-2">
              {allContainers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedContainerIds((s) => toggle(s, c.id))}
                  className={cn("px-3 py-1.5 rounded-xl text-xs font-medium border", selectedContainerIds.has(c.id) ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-neutral-border/40")}
                >
                  {c.name}
                </button>
              ))}
            </div>
            {siblings.length === 0 && (
              <p className="text-xs text-gray-400 mt-2">Tip: add more containers under the same room to redistribute across them.</p>
            )}
          </div>

          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">Items to place ({selectedItemIds.size})</p>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {items.map((it) => (
                <button key={it.id} onClick={() => setSelectedItemIds((s) => toggle(s, it.id))} className={cn("w-full text-left px-3 py-1.5 rounded-lg text-sm", selectedItemIds.has(it.id) ? "bg-primary/10 text-primary font-medium" : "hover:bg-gray-50")}>
                  {selectedItemIds.has(it.id) ? "✓ " : ""}{it.name}
                </button>
              ))}
              {items.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No items in this container yet.</p>}
            </div>
          </div>

          <button onClick={run} disabled={!canRun} className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Planning…" : "Suggest a plan"}
          </button>
        </>
      )}

      {result && (
        <div className="space-y-4">
          <div className={cn("text-sm font-medium px-3 py-2 rounded-xl border",
            result.fits === "yes" ? "bg-green-50 text-green-700 border-green-200" :
            result.fits === "likely" ? "bg-amber-50 text-amber-700 border-amber-200" :
            result.fits === "no" ? "bg-red-50 text-red-700 border-red-200" :
            "bg-gray-50 text-gray-600 border-gray-200")}
          >
            {result.fits === "yes" && "Everything fits comfortably."}
            {result.fits === "likely" && "Should fit — a couple of containers are tight."}
            {result.fits === "no" && "Not everything fits — see overflow below."}
            {result.fits === "unknown" && "Can't fully confirm fit — some dimensions are missing or uncertain."}
          </div>

          {result.assignments.map((a) => (
            <div key={a.containerId} className="rounded-xl border border-neutral-border/30 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{a.containerLabel}</span>
                <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full border", FIT_STYLES[a.fit])}>
                  {FIT_LABEL[a.fit]} · conf {(a.fitConfidence * 100).toFixed(0)}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={cn("h-full", a.fit === "wont_fit" ? "bg-red-400" : a.fit === "tight" ? "bg-amber-400" : "bg-green-400")} style={{ width: `${Math.min(100, a.fill * 100)}%` }} />
              </div>
              <p className="text-xs text-gray-500">{a.reasoning}</p>
              <div className="flex flex-wrap gap-1">
                {a.items.map((it) => <span key={it.itemId} className="text-xs bg-gray-50 border border-neutral-border/30 rounded-lg px-2 py-0.5">{it.name}</span>)}
              </div>
            </div>
          ))}

          {result.overflow.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-700 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Won't fit ({result.overflow.length})</p>
              <p className="text-xs text-red-600">{result.overflow.map((o) => o.name).join(", ")} — add a container or remove something.</p>
            </div>
          )}

          {result.lowConfidence.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1"><HelpCircle className="w-3.5 h-3.5" />Can't judge fit yet ({result.lowConfidence.length})</p>
              <p className="text-xs text-gray-500">{result.lowConfidence.map((l) => l.name).join(", ")} — measure or confirm dimensions.</p>
            </div>
          )}

          {(result.alreadyOptimal?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Already in the best spot ({result.alreadyOptimal.length})</p>
              <p className="text-xs text-amber-700">
                {result.alreadyOptimal.map((o) => o.name).join(", ")} — we don't see a better container among your choices.
                If a spot isn't working, add another container to compare, or note what's wrong so we can learn.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => setResult(null)} className="flex-1 py-2 rounded-xl bg-primary-light text-primary text-sm font-medium">Adjust</button>
            <button onClick={apply} disabled={applying || result.assignments.length === 0} className="flex-1 py-2 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">
              {applying ? "Applying…" : "Apply plan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
